<template>
  <div v-if="currDataArray && currDataArray.length > 0">
    <HgSelect
      v-model="selectedRangeOption"
      :options="yearRangeFilter"
      name="select"
      :class="$style.selectFilter"
      @update:modelValue="filterData"
    />
    <HgSkeleton v-if="isLoading" height="195px" :class="$style.loaderChart" />
    <chart
      v-else
      :chartType="'bar'"
      :fill="chartFill"
      :isIS24="false"
      :plotOptions="plotOptions"
      :stroke="chartStroke"
      :filterRange="selectedRange"
      @currentDevelopment="handleCurrentDevelopmentChange"
      @developmentStartTime="handleDevelopmentStartTime"
    />
    <div :class="$style.divider" />
    <HgSkeleton v-if="isLoading" height="51px" />
    <div v-else :class="$style.summaryWrapper" data-testid="summary">
      <div>
        <p :class="$style.chartHeading">{{ t('priceTrends.heading') }}</p>
        <p :class="$style.summary" v-html="t('priceTrends.summary', [timePeriod])" />
      </div>
      <HgBadge
        :theme="developmentType == 'increased' ? 'greenlight' : 'orange'"
        :class="[
          $style.badge,
          {
            [$style.increased]: developmentType === 'increased',
            [$style.decreased]: developmentType === 'decreased',
          },
        ]"
      >
        {{ currDevelopmentText }}
        <HgIcon icon="TrendingUp" :rotation="developmentType == 'increased' ? 0 : 180" />
      </HgBadge>
    </div>
  </div>
  <div v-else :class="$style.noDataContainer">
    <span :class="$style.summary" data-testid="noData">{{ t('common.noData') }}</span>
  </div>
</template>
<script setup lang="ts">
import {
  HgBadge,
  HgIcon,
  HgSelect,
  type HgSelectOptions,
  HgSkeleton,
} from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { type PriceEvolutionDataItem } from '@/models/iazi/queries/getPriceEvolutionResponse';
import useMapStore from '@/stores/useMapStore';
import { trackPriceTrendYearSelection } from '@/tracking';

import chart from './ChartComponent.vue';

const mapStore = useMapStore();
const { data, propertyType, isLoading } = storeToRefs(mapStore);
const { t, locale } = useI18n();
const selectedRange = ref<number>(0);
const selectedRangeOption = ref<string>('0');
const currDevelopment = ref<number>(0);
const startTime = ref<number | string>(0);

/* define computed variables */
const currDataArray = computed<Array<PriceEvolutionDataItem> | undefined>(
  () => data.value.priceEvoData?.results?.[propertyType.value],
);
const handleDevelopmentStartTime = (value: number | string) => {
  startTime.value = value;
};
const handleCurrentDevelopmentChange = (value: number) => {
  currDevelopment.value = value;
};

const developmentType = computed<'increased' | 'decreased'>(() => {
  return currDevelopment.value >= 0 ? 'increased' : 'decreased';
});

const currDevelopmentText = computed<string>(() => {
  if (developmentType.value === 'increased') {
    return `+ ${currDevelopment.value}%`;
  } else {
    return `- ${Math.abs(currDevelopment.value)}%`;
  }
});

const timePeriod = computed<string>(() => {
  if (selectedRange.value === 0.5) {
    return '6 ' + t('priceTrends.months');
  } else if (selectedRange.value === 1) {
    return '1 ' + t('priceTrends.year');
  } else {
    return selectedRange.value + ' ' + t('priceTrends.years');
  }
});

const plotOptions = {
  bar: {
    columnWidth: '8px',
    borderRadius: 3,
  },
};
const chartFill = {
  type: 'solid',
  colors: ['#1E50AD'],
};
const chartStroke = {
  width: 2,
  colors: ['transparent'],
};

const yearRangeFilter = computed<HgSelectOptions[]>(() => {
  const responseArray = currDataArray.value;
  const len = responseArray?.length ?? 0;
  switch (len) {
    case 0:
    case 1:
    case 2:
      return [];
    case 3:
      return buildSelectOptions(1);
    case 4:
    case 5:
      return buildSelectOptions(2);
    case 6:
    case 7:
      return buildSelectOptions(3);
    default:
      return buildSelectOptions(4);
  }
});
/* functions are defined here */
const filterData = async (value: string) => {
  selectedRange.value = parseFloat(value);
  //pass to the child component
  mapStore.setSelectedFilterRange(selectedRange.value);
  trackPriceTrendYearSelection(selectedRange.value);
};
const buildSelectOptions = (value: number): Array<HgSelectOptions> => {
  let tempObj: HgSelectOptions = { value: '', text: '' };
  const segArray: Array<HgSelectOptions> = [];
  selectedRange.value = value;
  selectedRangeOption.value = value.toString();
  for (let i = value; i <= value && i > 0; i--) {
    tempObj = {
      value: i.toString(),
      text: i === 1 ? i + ' ' + t('priceTrends.year') : i + ' ' + t('priceTrends.years'),
    };
    segArray.push(tempObj);
  }
  tempObj = {
    value: '0.5',
    text: '6 ' + t('priceTrends.months'),
  };
  segArray.push(tempObj);
  return segArray;
};
const calculateCurrDevelopment = (responseArray: Array<PriceEvolutionDataItem>) => {
  currDevelopment.value = 0;
  startTime.value = 0;
  currDevelopment.value = responseArray[responseArray.length - 1].evolution;
  const element = responseArray[0];
  if (element.timePeriod % 100 == 12) {
    startTime.value = Math.round(element.timePeriod / 100) + 1;
  } else {
    startTime.value = t('priceTrends.midYear', [Math.round(element.timePeriod / 100)]).toString();
  }
};
watch(
  currDataArray,
  () => {
    if (currDataArray.value && currDataArray.value.length > 1) {
      mapStore.setSelectedFilterRange(0);
      calculateCurrDevelopment(currDataArray.value);
    }
  },
  { immediate: true, deep: true },
);
watch(locale, () => {
  mapStore.setSelectedFilterRange(selectedRange.value);
});
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.noDataContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 280px;
}

.summary {
  margin-top: 0;
  margin-bottom: 0;
  color: $color-grey-100;

  @include body-3;
}

.chartHeading {
  margin-top: 0;
  margin-bottom: 0;
  color: $color-grey-100;

  @include heading-5;
}

.selectFilter {
  max-width: 116px;
}

.badge {
  align-self: baseline;

  @include body-2-bold;

  &[class*='HgBadge_badge'] {
    padding: $space-75;
  }

  svg {
    margin-left: $space-50;
  }

  &.increased {
    svg path {
      fill: $color-green-100;
    }
  }

  &.decreased {
    svg path {
      fill: $color-orange-90;
    }
  }
}

.summaryWrapper {
  display: flex;
  gap: $space-100;
  justify-content: space-between;
}

.divider {
  width: 100%;
  height: 1px;
  margin-top: $space-50;
  margin-bottom: $space-150;
  background-color: $color-grey-60;
}

.loaderChart {
  margin-top: $space-75;
  margin-bottom: $space-75;
}
</style>
